// English lessons for learners of English, hand-written (no API cost).
// Structure: lessonsEnglish[interfaceLanguage][lessonSlug]
// Same slide format as the Italian lessons in Lesson.js.
export const lessonsEnglish = {
    "en": {
        "hello-and-goodbye": {
            "title": "Hello and goodbye",
            "topic": "Greetings & introductions",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Hello and goodbye in English",
                    "body": "English speakers greet each other differently depending on the time of day and the situation. Let's learn the essentials.",
                    "emoji": "👋"
                },
                {
                    "type": "vocabulary",
                    "word": "Hello",
                    "translation": "A standard greeting, safe in any situation",
                    "phonetic": "/həˈloʊ/",
                    "note": "Works with anyone: friends, strangers, your boss. It is neutral, neither too formal nor too casual.",
                    "emoji": "👋"
                },
                {
                    "type": "vocabulary",
                    "word": "Hi",
                    "translation": "A casual way to say hello",
                    "phonetic": "/haɪ/",
                    "note": "Friendly and very common. Fine with colleagues and friends, a little casual for a first formal meeting.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "Good morning",
                    "translation": "Greeting used until about noon",
                    "phonetic": "/ɡʊd ˈmɔːrnɪŋ/",
                    "note": "Used from the moment you wake up until around 12:00. After that, switch to \"good afternoon\".",
                    "emoji": "🌅"
                },
                {
                    "type": "vocabulary",
                    "word": "Goodbye",
                    "translation": "A standard way to end a conversation",
                    "phonetic": "/ɡʊdˈbaɪ/",
                    "note": "Polite and neutral. Friends often say just \"bye\" instead.",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "See you later",
                    "translation": "Informal goodbye when you expect to meet again",
                    "phonetic": "/siː juː ˈleɪtər/",
                    "note": "Friendly and relaxed. You do not have to mean \"later today\": it is just a warm goodbye.",
                    "emoji": "😎"
                },
                {
                    "type": "question",
                    "question": "Which greeting is best for a first meeting with your new boss?",
                    "options": [
                        "See you later",
                        "Hi!",
                        "Good morning",
                        "Bye"
                    ],
                    "correct": 2,
                    "explanation": "\"Good morning\" is polite and fits a professional setting. \"See you later\" and \"Bye\" are for leaving, not arriving."
                },
                {
                    "type": "question",
                    "question": "Which of these is the most casual greeting?",
                    "options": [
                        "Good morning",
                        "Hello",
                        "Good evening",
                        "Hi"
                    ],
                    "correct": 3,
                    "explanation": "\"Hi\" is the shortest and most casual option."
                },
                {
                    "type": "question",
                    "question": "You are leaving a friend and expect to see them again soon. What do you say?",
                    "options": [
                        "Good morning",
                        "See you later",
                        "Nice to meet you",
                        "Excuse me"
                    ],
                    "correct": 1,
                    "explanation": "\"See you later\" is a friendly goodbye when you expect to meet again."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You now know the core English greetings and when to use them. Next up: how to say your name.",
                    "xp": 15
                }
            ]
        },
        "whats-your-name": {
            "title": "What's your name?",
            "topic": "Greetings & introductions",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Introducing yourself in English",
                    "body": "Saying your name is one of the first things you do in any language. Good news: English has one simple question for everyone, with no formal or informal version.",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "What's your name?",
                    "translation": "The standard question to ask someone's name",
                    "phonetic": "/wʌts jɔːr neɪm/",
                    "note": "Use it with anyone. English has no \"tu\" and \"Lei\" difference, so there is only one version.",
                    "emoji": "💬"
                },
                {
                    "type": "vocabulary",
                    "word": "My name is...",
                    "translation": "The standard way to give your name",
                    "phonetic": "/maɪ neɪm ɪz/",
                    "note": "Clear and slightly more formal. \"My name is Andrea.\"",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm...",
                    "translation": "A shorter, more casual way to say your name",
                    "phonetic": "/aɪm/",
                    "note": "Short for \"I am\". \"I'm Andrea\" sounds natural and relaxed.",
                    "emoji": "😎"
                },
                {
                    "type": "vocabulary",
                    "word": "And you?",
                    "translation": "A quick way to return a question",
                    "phonetic": "/ænd juː/",
                    "note": "After someone gives their name, you can answer and ask back: \"I'm Sara. And you?\"",
                    "emoji": "🔁"
                },
                {
                    "type": "vocabulary",
                    "word": "How do you spell that?",
                    "translation": "Asking someone to spell a word or name",
                    "phonetic": "/haʊ duː juː spɛl ðæt/",
                    "note": "Very useful for names you have never heard before.",
                    "emoji": "🔤"
                },
                {
                    "type": "question",
                    "question": "Someone asks \"What's your name?\" Which answer is correct?",
                    "options": [
                        "I have Marco.",
                        "My name is Marco.",
                        "I call Marco.",
                        "Me Marco name."
                    ],
                    "correct": 1,
                    "explanation": "\"My name is...\" is the standard answer. The other options are not correct English."
                },
                {
                    "type": "question",
                    "question": "Which version sounds the most relaxed?",
                    "options": [
                        "My name is Giulia.",
                        "I'm Giulia.",
                        "Giulia is my name.",
                        "I am called Giulia."
                    ],
                    "correct": 1,
                    "explanation": "\"I'm Giulia\" is short and casual. The others are correct but more formal."
                },
                {
                    "type": "question",
                    "question": "You didn't catch someone's name. What can you ask?",
                    "options": [
                        "How do you spell that?",
                        "Where are you from?",
                        "How old are you?",
                        "See you later."
                    ],
                    "correct": 0,
                    "explanation": "\"How do you spell that?\" asks the person to spell their name letter by letter."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now ask for and give names in English. Next up: saying where you are from.",
                    "xp": 15
                }
            ]
        },
        "where-are-you-from": {
            "title": "Where are you from?",
            "topic": "Greetings & introductions",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Talking about where you are from",
                    "body": "After names, the next question is usually about where you come from and where you live. Careful: English uses different words for each.",
                    "emoji": "🌍"
                },
                {
                    "type": "vocabulary",
                    "word": "Where are you from?",
                    "translation": "Asking about someone's country or hometown",
                    "phonetic": "/wɛr ɑːr juː frʌm/",
                    "note": "Asks about where you come from, not where you live right now.",
                    "emoji": "🌍"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm from Italy.",
                    "translation": "Saying your country of origin",
                    "phonetic": "/aɪm frʌm ˈɪtəli/",
                    "note": "Pattern: \"I'm from\" + country or city. Always use \"from\", not \"of\".",
                    "emoji": "🇮🇹"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm Italian.",
                    "translation": "Saying your nationality",
                    "phonetic": "/aɪm ɪˈtæljən/",
                    "note": "Nationalities are adjectives and always start with a capital letter: Italian, Spanish, French, Brazilian.",
                    "emoji": "🧑"
                },
                {
                    "type": "vocabulary",
                    "word": "Where do you live?",
                    "translation": "Asking where someone lives now",
                    "phonetic": "/wɛr duː juː lɪv/",
                    "note": "Different from \"Where are you from?\": this one is about your current home.",
                    "emoji": "🏠"
                },
                {
                    "type": "vocabulary",
                    "word": "I live in Verona.",
                    "translation": "Saying where you live",
                    "phonetic": "/aɪ lɪv ɪn vɛˈroʊnə/",
                    "note": "Pattern: \"I live in\" + city or country.",
                    "emoji": "📍"
                },
                {
                    "type": "question",
                    "question": "Which sentence is correct?",
                    "options": [
                        "I'm from Spain.",
                        "I'm of Spain.",
                        "I'm at Spain.",
                        "I'm to Spain."
                    ],
                    "correct": 0,
                    "explanation": "\"I'm from\" + place is the correct pattern."
                },
                {
                    "type": "question",
                    "question": "Which question asks where someone lives right now?",
                    "options": [
                        "Where are you from?",
                        "Where do you live?",
                        "Who are you?",
                        "How do you do?"
                    ],
                    "correct": 1,
                    "explanation": "\"Where do you live?\" is about the present. \"Where are you from?\" is about your origin."
                },
                {
                    "type": "question",
                    "question": "Which of these is a nationality?",
                    "options": [
                        "France",
                        "French",
                        "Frances",
                        "Frenchland"
                    ],
                    "correct": 1,
                    "explanation": "\"French\" is the nationality. \"France\" is the country."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now talk about where you are from and where you live. Next up: asking how someone is.",
                    "xp": 15
                }
            ]
        },
        "how-are-you": {
            "title": "How are you?",
            "topic": "Greetings & introductions",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Asking and answering \"How are you?\"",
                    "body": "In English, \"How are you?\" is often just a greeting, not a real question. People usually answer with something short and positive.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "How are you?",
                    "translation": "The standard way to ask how someone is",
                    "phonetic": "/haʊ ɑːr juː/",
                    "note": "Often used as a greeting. Nobody expects a long answer.",
                    "emoji": "💬"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm fine, thanks.",
                    "translation": "A polite, neutral answer",
                    "phonetic": "/aɪm faɪn θæŋks/",
                    "note": "Safe in every situation. Add \"And you?\" to be polite.",
                    "emoji": "👍"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm great!",
                    "translation": "A very positive answer",
                    "phonetic": "/aɪm ɡreɪt/",
                    "note": "Use it when you really feel good. \"Great\" is stronger than \"fine\".",
                    "emoji": "🤩"
                },
                {
                    "type": "vocabulary",
                    "word": "Not bad.",
                    "translation": "Fairly good, a relaxed answer",
                    "phonetic": "/nɒt bæd/",
                    "note": "Very common, especially in British English. It means \"pretty good\".",
                    "emoji": "🙂"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a bit tired.",
                    "translation": "Being honest that you are not at your best",
                    "phonetic": "/aɪm ə bɪt ˈtaɪərd/",
                    "note": "\"A bit\" softens the feeling. Use it with friends or colleagues you know.",
                    "emoji": "😴"
                },
                {
                    "type": "question",
                    "question": "Someone says \"How are you?\" as you walk past. What is the most natural reply?",
                    "options": [
                        "I'm fine, thanks. And you?",
                        "Let me tell you about my week.",
                        "My name is Anna.",
                        "Goodbye."
                    ],
                    "correct": 0,
                    "explanation": "A short, positive answer plus \"And you?\" is what people expect."
                },
                {
                    "type": "question",
                    "question": "Which answer is stronger than \"I'm fine\"?",
                    "options": [
                        "Not bad",
                        "I'm a bit tired",
                        "I'm great!",
                        "I'm okay"
                    ],
                    "correct": 2,
                    "explanation": "\"Great\" shows more enthusiasm than \"fine\"."
                },
                {
                    "type": "question",
                    "question": "What does \"Not bad\" usually mean?",
                    "options": [
                        "Quite good",
                        "Very sad",
                        "Extremely angry",
                        "I do not understand"
                    ],
                    "correct": 0,
                    "explanation": "\"Not bad\" is a relaxed way to say things are fairly good."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now ask people how they are and answer naturally. Next up: saying nice to meet you.",
                    "xp": 15
                }
            ]
        },
        "nice-to-meet-you": {
            "title": "Nice to meet you",
            "topic": "Greetings & introductions",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Meeting someone for the first time",
                    "body": "A few set phrases help you sound polite when you meet someone new. They are easy to learn and you will use them constantly.",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "Nice to meet you.",
                    "translation": "The standard phrase when meeting someone",
                    "phonetic": "/naɪs tuː miːt juː/",
                    "note": "Use it right after you hear the person's name. Friendly and safe everywhere.",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "Nice to meet you too.",
                    "translation": "The natural reply to \"Nice to meet you\"",
                    "phonetic": "/naɪs tuː miːt juː tuː/",
                    "note": "Do not forget \"too\". It means \"the same to you\".",
                    "emoji": "😄"
                },
                {
                    "type": "vocabulary",
                    "word": "Pleased to meet you.",
                    "translation": "A slightly more formal version",
                    "phonetic": "/pliːzd tuː miːt juː/",
                    "note": "Good for business or formal events.",
                    "emoji": "🎩"
                },
                {
                    "type": "vocabulary",
                    "word": "It's a pleasure.",
                    "translation": "A polished, warm way to greet someone",
                    "phonetic": "/ɪts ə ˈplɛʒər/",
                    "note": "Often heard in formal or professional settings.",
                    "emoji": "✨"
                },
                {
                    "type": "vocabulary",
                    "word": "How do you do?",
                    "translation": "A very formal greeting, not a real question",
                    "phonetic": "/haʊ duː juː duː/",
                    "note": "Rarely used today. The reply is usually the same phrase: \"How do you do?\"",
                    "emoji": "🧐"
                },
                {
                    "type": "question",
                    "question": "Someone says \"Nice to meet you.\" What is the best reply?",
                    "options": [
                        "Nice to meet you too.",
                        "I am fine.",
                        "See you later.",
                        "Excuse me."
                    ],
                    "correct": 0,
                    "explanation": "\"Nice to meet you too\" is the natural reply."
                },
                {
                    "type": "question",
                    "question": "Which phrase is the most formal?",
                    "options": [
                        "Hi!",
                        "Nice to meet you",
                        "Pleased to meet you",
                        "See you"
                    ],
                    "correct": 2,
                    "explanation": "\"Pleased to meet you\" is a little more formal than \"Nice to meet you\"."
                },
                {
                    "type": "question",
                    "question": "Is \"How do you do?\" a real question about someone's health?",
                    "options": [
                        "Yes, always",
                        "No, it is a formal greeting",
                        "Yes, but only in the morning",
                        "It means goodbye"
                    ],
                    "correct": 1,
                    "explanation": "It is a formal greeting. You usually just repeat it back."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now greet new people politely. Next up: talking about your job.",
                    "xp": 15
                }
            ]
        },
        "talking-about-your-job": {
            "title": "Talking about your job",
            "topic": "Greetings & introductions",
            "duration": "7 min",
            "xp": 20,
            "slides": [
                {
                    "type": "intro",
                    "title": "What do you do?",
                    "body": "After names and origins, work is one of the most common topics. In English, \"What do you do?\" means \"What is your job?\".",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "What do you do?",
                    "translation": "Asking about someone's job",
                    "phonetic": "/wʌt duː juː duː/",
                    "note": "It sounds odd, but it means \"What is your job?\". The answer is a job, not an activity.",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a teacher.",
                    "translation": "Saying your job",
                    "phonetic": "/aɪm ə ˈtiːtʃər/",
                    "note": "English needs \"a\" or \"an\" before the job: \"I'm a teacher\", never \"I'm teacher\".",
                    "emoji": "👩‍🏫"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm an engineer.",
                    "translation": "Using \"an\" before a vowel sound",
                    "phonetic": "/aɪm ən ˌɛndʒɪˈnɪr/",
                    "note": "Use \"an\" before a vowel sound: an engineer, an artist, an accountant.",
                    "emoji": "🛠️"
                },
                {
                    "type": "vocabulary",
                    "word": "I work in an office.",
                    "translation": "Describing where you work",
                    "phonetic": "/aɪ wɜːrk ɪn ən ˈɒfɪs/",
                    "note": "Useful when you do not want to name your exact job. Pattern: \"I work in\" + place.",
                    "emoji": "🏢"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a student.",
                    "translation": "Saying you are studying",
                    "phonetic": "/aɪm ə ˈstuːdənt/",
                    "note": "You can add where: \"I'm a student at the University of Verona.\"",
                    "emoji": "🎓"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm between jobs.",
                    "translation": "A polite way to say you are unemployed",
                    "phonetic": "/aɪm bɪˈtwiːn dʒɒbz/",
                    "note": "Sounds softer and more positive than \"I'm unemployed\".",
                    "emoji": "🔍"
                },
                {
                    "type": "question",
                    "question": "What does \"What do you do?\" mean in a conversation?",
                    "options": [
                        "What are you doing right now?",
                        "What is your job?",
                        "Where do you live?",
                        "How are you?"
                    ],
                    "correct": 1,
                    "explanation": "It asks about your job."
                },
                {
                    "type": "question",
                    "question": "Which sentence is correct?",
                    "options": [
                        "I'm teacher.",
                        "I'm a teacher.",
                        "I'm the teacher job.",
                        "I teacher."
                    ],
                    "correct": 1,
                    "explanation": "English needs \"a\" before a job."
                },
                {
                    "type": "question",
                    "question": "Which is correct?",
                    "options": [
                        "I'm a engineer.",
                        "I'm an engineer.",
                        "I'm engineer.",
                        "I'm the engineer job."
                    ],
                    "correct": 1,
                    "explanation": "\"Engineer\" starts with a vowel sound, so we use \"an\"."
                },
                {
                    "type": "question",
                    "question": "Which phrase is a polite way to say you do not have a job right now?",
                    "options": [
                        "I'm between jobs.",
                        "I'm a student.",
                        "I work in an office.",
                        "I'm an engineer."
                    ],
                    "correct": 0,
                    "explanation": "\"Between jobs\" is a gentle way to say unemployed."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now talk about your job and ask others about theirs. Next up: asking someone's age.",
                    "xp": 20
                }
            ]
        },
        "asking-someones-age": {
            "title": "Asking someone's age",
            "topic": "Greetings & introductions",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Talking about age",
                    "body": "Age works differently in English than in Italian. You do not \"have\" years, you \"are\" years old.",
                    "emoji": "🎂"
                },
                {
                    "type": "vocabulary",
                    "word": "How old are you?",
                    "translation": "Asking someone's age",
                    "phonetic": "/haʊ oʊld ɑːr juː/",
                    "note": "Fine among friends or in forms. With adults you do not know well, it can feel personal.",
                    "emoji": "🎂"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm thirty.",
                    "translation": "Saying your age",
                    "phonetic": "/aɪm ˈθɜːrti/",
                    "note": "Use \"I am\" + number. You can add \"years old\": \"I'm thirty years old.\"",
                    "emoji": "3️⃣"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm twenty-five years old.",
                    "translation": "A full way to state your age",
                    "phonetic": "/aɪm ˈtwɛnti faɪv jɪrz oʊld/",
                    "note": "Never say \"I have twenty-five years\": it is a common mistake for Italian speakers.",
                    "emoji": "🔢"
                },
                {
                    "type": "vocabulary",
                    "word": "How old is she?",
                    "translation": "Asking about a third person's age",
                    "phonetic": "/haʊ oʊld ɪz ʃiː/",
                    "note": "Change \"are\" to \"is\" for he, she and it.",
                    "emoji": "👩"
                },
                {
                    "type": "vocabulary",
                    "word": "She's in her twenties.",
                    "translation": "An approximate age",
                    "phonetic": "/ʃiːz ɪn hɜːr ˈtwɛntiz/",
                    "note": "A polite way to guess without giving an exact number.",
                    "emoji": "🧑‍🤝‍🧑"
                },
                {
                    "type": "question",
                    "question": "Which sentence is correct?",
                    "options": [
                        "I have thirty years.",
                        "I'm thirty years old.",
                        "I am thirty years.",
                        "I have thirty old."
                    ],
                    "correct": 1,
                    "explanation": "In English we use \"be\" for age: \"I'm thirty years old.\""
                },
                {
                    "type": "question",
                    "question": "How do you ask about a man's age?",
                    "options": [
                        "How old are he?",
                        "How old is he?",
                        "How many years has he?",
                        "What age he is?"
                    ],
                    "correct": 1,
                    "explanation": "With \"he\", we use \"is\": \"How old is he?\""
                },
                {
                    "type": "question",
                    "question": "What does \"in her twenties\" mean?",
                    "options": [
                        "Aged 20 to 29",
                        "Aged exactly 20",
                        "Aged 12",
                        "Aged over 50"
                    ],
                    "correct": 0,
                    "explanation": "\"In her twenties\" means between 20 and 29."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now talk about age the English way. Next up: saying please and thank you.",
                    "xp": 15
                }
            ]
        },
        "saying-please-and-thank-you": {
            "title": "Saying please and thank you",
            "topic": "Greetings & introductions",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Being polite in English",
                    "body": "English speakers say \"please\" and \"thank you\" all the time, even in small everyday situations. It is an important part of sounding natural.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Please",
                    "translation": "Added to a request to make it polite",
                    "phonetic": "/pliːz/",
                    "note": "\"A coffee, please.\" You can put it at the start or the end of a request.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Thank you",
                    "translation": "The standard way to show thanks",
                    "phonetic": "/θæŋk juː/",
                    "note": "Always appropriate, formal or informal.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "Thanks a lot",
                    "translation": "A warmer, more enthusiastic thanks",
                    "phonetic": "/θæŋks ə lɒt/",
                    "note": "Good when someone really helped you. \"Thanks so much\" is similar.",
                    "emoji": "🥰"
                },
                {
                    "type": "vocabulary",
                    "word": "You're welcome",
                    "translation": "The standard reply to thank you",
                    "phonetic": "/jɔːr ˈwɛlkəm/",
                    "note": "Do not say \"welcome\" alone. The full phrase is \"You're welcome\".",
                    "emoji": "🤗"
                },
                {
                    "type": "vocabulary",
                    "word": "No problem",
                    "translation": "A casual reply to thanks",
                    "phonetic": "/noʊ ˈprɒbləm/",
                    "note": "Friendly and relaxed. \"No worries\" works too, mostly in British and Australian English.",
                    "emoji": "👌"
                },
                {
                    "type": "question",
                    "question": "Which request is the most polite?",
                    "options": [
                        "Water.",
                        "Give me water.",
                        "Water, please.",
                        "I want water."
                    ],
                    "correct": 2,
                    "explanation": "Adding \"please\" makes the request polite."
                },
                {
                    "type": "question",
                    "question": "Someone says \"Thank you.\" What is the correct reply?",
                    "options": [
                        "You're welcome.",
                        "Please.",
                        "Hello.",
                        "Goodbye."
                    ],
                    "correct": 0,
                    "explanation": "\"You're welcome\" is the standard reply."
                },
                {
                    "type": "question",
                    "question": "Which reply is the most casual?",
                    "options": [
                        "You're welcome",
                        "It is my pleasure",
                        "No problem",
                        "Thank you very much"
                    ],
                    "correct": 2,
                    "explanation": "\"No problem\" is relaxed and informal."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now be polite in everyday English. Next up: how to apologize.",
                    "xp": 15
                }
            ]
        },
        "apologizing-and-excusing-yourself": {
            "title": "Apologizing and excusing yourself",
            "topic": "Greetings & introductions",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Sorry and excuse me",
                    "body": "English has two key words that look similar but do different jobs: \"sorry\" for mistakes, and \"excuse me\" for getting attention or passing by.",
                    "emoji": "🙇"
                },
                {
                    "type": "vocabulary",
                    "word": "Sorry",
                    "translation": "A quick apology after a small mistake",
                    "phonetic": "/ˈsɒri/",
                    "note": "Say it when you bump into someone or make a small error.",
                    "emoji": "😔"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm so sorry",
                    "translation": "A stronger, more sincere apology",
                    "phonetic": "/aɪm soʊ ˈsɒri/",
                    "note": "Use it for bigger mistakes or to show real regret.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Excuse me",
                    "translation": "Getting attention or passing someone politely",
                    "phonetic": "/ɪkˈskjuːz miː/",
                    "note": "Use it before you speak to a stranger or when you need to get past someone.",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "Pardon?",
                    "translation": "Asking someone to repeat what they said",
                    "phonetic": "/ˈpɑːrdn/",
                    "note": "Polite way to say you did not hear. \"Sorry?\" works too.",
                    "emoji": "👂"
                },
                {
                    "type": "vocabulary",
                    "word": "That's okay",
                    "translation": "Accepting an apology",
                    "phonetic": "/ðæts oʊˈkeɪ/",
                    "note": "\"No problem\" and \"Don't worry about it\" are good alternatives.",
                    "emoji": "😌"
                },
                {
                    "type": "question",
                    "question": "You step on someone's foot by accident. What do you say?",
                    "options": [
                        "Excuse me",
                        "Sorry",
                        "Pardon the foot",
                        "Please"
                    ],
                    "correct": 1,
                    "explanation": "\"Sorry\" is the quick apology after a small accident."
                },
                {
                    "type": "question",
                    "question": "You want to ask a stranger for directions. How do you start?",
                    "options": [
                        "Sorry you",
                        "Excuse me",
                        "I'm so sorry",
                        "Goodbye"
                    ],
                    "correct": 1,
                    "explanation": "\"Excuse me\" gets someone's attention politely."
                },
                {
                    "type": "question",
                    "question": "You did not hear what someone said. What can you say?",
                    "options": [
                        "Pardon?",
                        "Thank you.",
                        "See you later.",
                        "Nice to meet you."
                    ],
                    "correct": 0,
                    "explanation": "\"Pardon?\" asks the person to repeat."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now apologize and get attention politely. Next up: introducing someone else.",
                    "xp": 15
                }
            ]
        },
        "introducing-someone-else": {
            "title": "Introducing someone else",
            "topic": "Greetings & introductions",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Making introductions",
                    "body": "Introducing two people to each other is a useful social skill. In English you can do it in a simple, friendly way.",
                    "emoji": "👥"
                },
                {
                    "type": "vocabulary",
                    "word": "This is my friend, Sara.",
                    "translation": "The simplest way to introduce someone",
                    "phonetic": "/ðɪs ɪz maɪ frɛnd ˈsɑːrə/",
                    "note": "Pattern: \"This is\" + relationship + name.",
                    "emoji": "👭"
                },
                {
                    "type": "vocabulary",
                    "word": "This is my colleague.",
                    "translation": "Introducing someone you work with",
                    "phonetic": "/ðɪs ɪz maɪ ˈkɒliːɡ/",
                    "note": "\"Colleague\" is the neutral word for a coworker.",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "Let me introduce you to Tom.",
                    "translation": "A more polite and formal introduction",
                    "phonetic": "/lɛt miː ˌɪntrəˈdjuːs juː tuː tɒm/",
                    "note": "Good in professional situations.",
                    "emoji": "🎩"
                },
                {
                    "type": "vocabulary",
                    "word": "Have you met Anna?",
                    "translation": "Checking if two people already know each other",
                    "phonetic": "/hæv juː mɛt ˈænə/",
                    "note": "A natural way to start an introduction at a party.",
                    "emoji": "❓"
                },
                {
                    "type": "vocabulary",
                    "word": "I'd like you to meet my sister.",
                    "translation": "A warm, slightly formal way to introduce someone",
                    "phonetic": "/aɪd laɪk juː tuː miːt maɪ ˈsɪstər/",
                    "note": "Shows that the introduction matters to you.",
                    "emoji": "👩‍👧"
                },
                {
                    "type": "question",
                    "question": "Which sentence introduces a friend in the simplest way?",
                    "options": [
                        "This is my friend, Sara.",
                        "Sara my friend is.",
                        "I friend Sara.",
                        "Meet Sara friend."
                    ],
                    "correct": 0,
                    "explanation": "\"This is\" + relationship + name is the simplest pattern."
                },
                {
                    "type": "question",
                    "question": "Which question checks if two people already know each other?",
                    "options": [
                        "Have you met Anna?",
                        "Where is Anna?",
                        "Who are you?",
                        "How old is Anna?"
                    ],
                    "correct": 0,
                    "explanation": "\"Have you met...?\" is the natural way to check."
                },
                {
                    "type": "question",
                    "question": "Which phrase is the most formal?",
                    "options": [
                        "This is Tom.",
                        "Meet Tom.",
                        "Let me introduce you to Tom.",
                        "Tom, this is Ben."
                    ],
                    "correct": 2,
                    "explanation": "\"Let me introduce you to...\" is the most polite and formal option."
                },
                {
                    "type": "complete",
                    "title": "Lesson complete!",
                    "body": "You can now introduce people in English. You have finished the Greetings & introductions topic!",
                    "xp": 15
                }
            ]
        }
    },
    "it": {
        "hello-and-goodbye": {
            "title": "Ciao e arrivederci",
            "topic": "Saluti e presentazioni",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Salutare in inglese",
                    "body": "Chi parla inglese saluta in modo diverso a seconda dell'ora e della situazione. Impariamo le espressioni fondamentali.",
                    "emoji": "👋"
                },
                {
                    "type": "vocabulary",
                    "word": "Hello",
                    "translation": "Il saluto standard, adatto a ogni situazione",
                    "phonetic": "/həˈloʊ/",
                    "note": "Va bene con chiunque: amici, sconosciuti, il capo. È neutro, né troppo formale né troppo informale.",
                    "emoji": "👋"
                },
                {
                    "type": "vocabulary",
                    "word": "Hi",
                    "translation": "Un modo informale per dire ciao",
                    "phonetic": "/haɪ/",
                    "note": "Amichevole e molto comune. Va bene con colleghi e amici, un po' informale per un primo incontro formale.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "Good morning",
                    "translation": "Saluto usato fino a mezzogiorno circa",
                    "phonetic": "/ɡʊd ˈmɔːrnɪŋ/",
                    "note": "Si usa dal mattino fino alle 12 circa. Dopo si passa a \"good afternoon\".",
                    "emoji": "🌅"
                },
                {
                    "type": "vocabulary",
                    "word": "Goodbye",
                    "translation": "Un modo standard per concludere una conversazione",
                    "phonetic": "/ɡʊdˈbaɪ/",
                    "note": "Educato e neutro. Tra amici si dice spesso solo \"bye\".",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "See you later",
                    "translation": "Saluto informale quando pensi di rivedere la persona",
                    "phonetic": "/siː juː ˈleɪtər/",
                    "note": "Amichevole e rilassato. Non significa per forza \"più tardi oggi\": è solo un saluto caloroso.",
                    "emoji": "😎"
                },
                {
                    "type": "question",
                    "question": "Quale saluto è il migliore per il primo incontro con il tuo nuovo capo?",
                    "options": [
                        "See you later",
                        "Hi!",
                        "Good morning",
                        "Bye"
                    ],
                    "correct": 2,
                    "explanation": "\"Good morning\" è educato e adatto a un contesto professionale. \"See you later\" e \"Bye\" si usano per andare via, non per arrivare."
                },
                {
                    "type": "question",
                    "question": "Quale di questi è il saluto più informale?",
                    "options": [
                        "Good morning",
                        "Hello",
                        "Good evening",
                        "Hi"
                    ],
                    "correct": 3,
                    "explanation": "\"Hi\" è l'opzione più breve e informale."
                },
                {
                    "type": "question",
                    "question": "Stai salutando un amico che pensi di rivedere presto. Cosa dici?",
                    "options": [
                        "Good morning",
                        "See you later",
                        "Nice to meet you",
                        "Excuse me"
                    ],
                    "correct": 1,
                    "explanation": "\"See you later\" è un saluto amichevole quando pensi di rivedere la persona."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora conosci i saluti principali in inglese e quando usarli. Prossimo passo: dire il tuo nome.",
                    "xp": 15
                }
            ]
        },
        "whats-your-name": {
            "title": "Come ti chiami?",
            "topic": "Saluti e presentazioni",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Presentarsi in inglese",
                    "body": "Dire il proprio nome è una delle prime cose in qualsiasi lingua. Buona notizia: in inglese esiste una sola domanda per tutti, senza forma formale o informale.",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "What's your name?",
                    "translation": "La domanda standard per chiedere il nome",
                    "phonetic": "/wʌts jɔːr neɪm/",
                    "note": "Si usa con chiunque. L'inglese non distingue tra \"tu\" e \"Lei\", quindi esiste una sola versione.",
                    "emoji": "💬"
                },
                {
                    "type": "vocabulary",
                    "word": "My name is...",
                    "translation": "Il modo standard per dire il tuo nome",
                    "phonetic": "/maɪ neɪm ɪz/",
                    "note": "Chiaro e un po' più formale. \"My name is Andrea.\"",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm...",
                    "translation": "Un modo più breve e informale per dire il tuo nome",
                    "phonetic": "/aɪm/",
                    "note": "Forma contratta di \"I am\". \"I'm Andrea\" suona naturale e rilassato.",
                    "emoji": "😎"
                },
                {
                    "type": "vocabulary",
                    "word": "And you?",
                    "translation": "Un modo rapido per restituire una domanda",
                    "phonetic": "/ænd juː/",
                    "note": "Dopo che qualcuno dice il suo nome, puoi rispondere e restituire la domanda: \"I'm Sara. And you?\"",
                    "emoji": "🔁"
                },
                {
                    "type": "vocabulary",
                    "word": "How do you spell that?",
                    "translation": "Chiedere a qualcuno di fare lo spelling di una parola o un nome",
                    "phonetic": "/haʊ duː juː spɛl ðæt/",
                    "note": "Utilissima per nomi che non hai mai sentito.",
                    "emoji": "🔤"
                },
                {
                    "type": "question",
                    "question": "Qualcuno ti chiede \"What's your name?\" Quale risposta è corretta?",
                    "options": [
                        "I have Marco.",
                        "My name is Marco.",
                        "I call Marco.",
                        "Me Marco name."
                    ],
                    "correct": 1,
                    "explanation": "\"My name is...\" è la risposta standard. Le altre opzioni non sono inglese corretto."
                },
                {
                    "type": "question",
                    "question": "Quale versione suona più rilassata?",
                    "options": [
                        "My name is Giulia.",
                        "I'm Giulia.",
                        "Giulia is my name.",
                        "I am called Giulia."
                    ],
                    "correct": 1,
                    "explanation": "\"I'm Giulia\" è breve e informale. Le altre sono corrette ma più formali."
                },
                {
                    "type": "question",
                    "question": "Non hai capito il nome di una persona. Cosa puoi chiedere?",
                    "options": [
                        "How do you spell that?",
                        "Where are you from?",
                        "How old are you?",
                        "See you later."
                    ],
                    "correct": 0,
                    "explanation": "\"How do you spell that?\" chiede alla persona di fare lo spelling del nome lettera per lettera."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai chiedere e dire il nome in inglese. Prossimo passo: dire di dove sei.",
                    "xp": 15
                }
            ]
        },
        "where-are-you-from": {
            "title": "Di dove sei?",
            "topic": "Saluti e presentazioni",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Parlare della tua provenienza",
                    "body": "Dopo il nome, la domanda successiva riguarda di solito da dove vieni e dove vivi. Attenzione: l'inglese usa parole diverse per ciascuna.",
                    "emoji": "🌍"
                },
                {
                    "type": "vocabulary",
                    "word": "Where are you from?",
                    "translation": "Chiedere il paese o la città d'origine di qualcuno",
                    "phonetic": "/wɛr ɑːr juː frʌm/",
                    "note": "Chiede da dove vieni, non dove vivi adesso.",
                    "emoji": "🌍"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm from Italy.",
                    "translation": "Dire il tuo paese d'origine",
                    "phonetic": "/aɪm frʌm ˈɪtəli/",
                    "note": "Schema: \"I'm from\" + paese o città. Si usa sempre \"from\", mai \"of\".",
                    "emoji": "🇮🇹"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm Italian.",
                    "translation": "Dire la tua nazionalità",
                    "phonetic": "/aɪm ɪˈtæljən/",
                    "note": "Le nazionalità sono aggettivi e iniziano sempre con la maiuscola: Italian, Spanish, French, Brazilian.",
                    "emoji": "🧑"
                },
                {
                    "type": "vocabulary",
                    "word": "Where do you live?",
                    "translation": "Chiedere dove vive qualcuno adesso",
                    "phonetic": "/wɛr duː juː lɪv/",
                    "note": "Diversa da \"Where are you from?\": questa riguarda la tua casa attuale.",
                    "emoji": "🏠"
                },
                {
                    "type": "vocabulary",
                    "word": "I live in Verona.",
                    "translation": "Dire dove vivi",
                    "phonetic": "/aɪ lɪv ɪn vɛˈroʊnə/",
                    "note": "Schema: \"I live in\" + città o paese.",
                    "emoji": "📍"
                },
                {
                    "type": "question",
                    "question": "Quale frase è corretta?",
                    "options": [
                        "I'm from Spain.",
                        "I'm of Spain.",
                        "I'm at Spain.",
                        "I'm to Spain."
                    ],
                    "correct": 0,
                    "explanation": "\"I'm from\" + luogo è lo schema corretto."
                },
                {
                    "type": "question",
                    "question": "Quale domanda chiede dove vive qualcuno in questo momento?",
                    "options": [
                        "Where are you from?",
                        "Where do you live?",
                        "Who are you?",
                        "How do you do?"
                    ],
                    "correct": 1,
                    "explanation": "\"Where do you live?\" riguarda il presente. \"Where are you from?\" riguarda l'origine."
                },
                {
                    "type": "question",
                    "question": "Quale di questi è una nazionalità?",
                    "options": [
                        "France",
                        "French",
                        "Frances",
                        "Frenchland"
                    ],
                    "correct": 1,
                    "explanation": "\"French\" è la nazionalità. \"France\" è il paese."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai parlare di dove sei e dove vivi. Prossimo passo: chiedere come sta qualcuno.",
                    "xp": 15
                }
            ]
        },
        "how-are-you": {
            "title": "Come stai?",
            "topic": "Saluti e presentazioni",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Chiedere e rispondere a \"How are you?\"",
                    "body": "In inglese \"How are you?\" è spesso solo un saluto, non una vera domanda. Di solito si risponde con qualcosa di breve e positivo.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "How are you?",
                    "translation": "Il modo standard per chiedere come sta qualcuno",
                    "phonetic": "/haʊ ɑːr juː/",
                    "note": "Spesso usata come saluto. Nessuno si aspetta una risposta lunga.",
                    "emoji": "💬"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm fine, thanks.",
                    "translation": "Una risposta educata e neutra",
                    "phonetic": "/aɪm faɪn θæŋks/",
                    "note": "Va bene in ogni situazione. Aggiungi \"And you?\" per essere cortese.",
                    "emoji": "👍"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm great!",
                    "translation": "Una risposta molto positiva",
                    "phonetic": "/aɪm ɡreɪt/",
                    "note": "Usala quando stai davvero bene. \"Great\" è più forte di \"fine\".",
                    "emoji": "🤩"
                },
                {
                    "type": "vocabulary",
                    "word": "Not bad.",
                    "translation": "Abbastanza bene, una risposta rilassata",
                    "phonetic": "/nɒt bæd/",
                    "note": "Molto comune, soprattutto nell'inglese britannico. Significa \"abbastanza bene\".",
                    "emoji": "🙂"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a bit tired.",
                    "translation": "Dire onestamente che non sei al meglio",
                    "phonetic": "/aɪm ə bɪt ˈtaɪərd/",
                    "note": "\"A bit\" attenua la sensazione. Usala con amici o colleghi che conosci.",
                    "emoji": "😴"
                },
                {
                    "type": "question",
                    "question": "Qualcuno ti dice \"How are you?\" mentre passi. Qual è la risposta più naturale?",
                    "options": [
                        "I'm fine, thanks. And you?",
                        "Let me tell you about my week.",
                        "My name is Anna.",
                        "Goodbye."
                    ],
                    "correct": 0,
                    "explanation": "Una risposta breve e positiva con \"And you?\" è quello che ci si aspetta."
                },
                {
                    "type": "question",
                    "question": "Quale risposta è più forte di \"I'm fine\"?",
                    "options": [
                        "Not bad",
                        "I'm a bit tired",
                        "I'm great!",
                        "I'm okay"
                    ],
                    "correct": 2,
                    "explanation": "\"Great\" mostra più entusiasmo di \"fine\"."
                },
                {
                    "type": "question",
                    "question": "Cosa significa di solito \"Not bad\"?",
                    "options": [
                        "Quite good",
                        "Very sad",
                        "Extremely angry",
                        "I do not understand"
                    ],
                    "correct": 0,
                    "explanation": "\"Not bad\" è un modo rilassato per dire che le cose vanno abbastanza bene."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai chiedere come stanno gli altri e rispondere in modo naturale. Prossimo passo: dire piacere di conoscerti.",
                    "xp": 15
                }
            ]
        },
        "nice-to-meet-you": {
            "title": "Piacere di conoscerti",
            "topic": "Saluti e presentazioni",
            "duration": "5 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Conoscere qualcuno per la prima volta",
                    "body": "Poche frasi fisse ti aiutano a sembrare educato quando incontri qualcuno di nuovo. Sono facili da imparare e le userai di continuo.",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "Nice to meet you.",
                    "translation": "La frase standard quando si conosce qualcuno",
                    "phonetic": "/naɪs tuː miːt juː/",
                    "note": "Si usa subito dopo aver sentito il nome della persona. Cordiale e adatta a ogni contesto.",
                    "emoji": "🤝"
                },
                {
                    "type": "vocabulary",
                    "word": "Nice to meet you too.",
                    "translation": "La risposta naturale a \"Nice to meet you\"",
                    "phonetic": "/naɪs tuː miːt juː tuː/",
                    "note": "Non dimenticare \"too\". Significa \"anche a te\".",
                    "emoji": "😄"
                },
                {
                    "type": "vocabulary",
                    "word": "Pleased to meet you.",
                    "translation": "Una versione un po' più formale",
                    "phonetic": "/pliːzd tuː miːt juː/",
                    "note": "Adatta a incontri di lavoro o eventi formali.",
                    "emoji": "🎩"
                },
                {
                    "type": "vocabulary",
                    "word": "It's a pleasure.",
                    "translation": "Un modo elegante e caloroso di salutare",
                    "phonetic": "/ɪts ə ˈplɛʒər/",
                    "note": "Si sente spesso in contesti formali o professionali.",
                    "emoji": "✨"
                },
                {
                    "type": "vocabulary",
                    "word": "How do you do?",
                    "translation": "Un saluto molto formale, non una vera domanda",
                    "phonetic": "/haʊ duː juː duː/",
                    "note": "Oggi si usa raramente. La risposta è di solito la stessa frase: \"How do you do?\"",
                    "emoji": "🧐"
                },
                {
                    "type": "question",
                    "question": "Qualcuno dice \"Nice to meet you.\" Qual è la risposta migliore?",
                    "options": [
                        "Nice to meet you too.",
                        "I am fine.",
                        "See you later.",
                        "Excuse me."
                    ],
                    "correct": 0,
                    "explanation": "\"Nice to meet you too\" è la risposta naturale."
                },
                {
                    "type": "question",
                    "question": "Quale frase è la più formale?",
                    "options": [
                        "Hi!",
                        "Nice to meet you",
                        "Pleased to meet you",
                        "See you"
                    ],
                    "correct": 2,
                    "explanation": "\"Pleased to meet you\" è un po' più formale di \"Nice to meet you\"."
                },
                {
                    "type": "question",
                    "question": "\"How do you do?\" è una vera domanda sulla salute di qualcuno?",
                    "options": [
                        "Yes, always",
                        "No, it is a formal greeting",
                        "Yes, but only in the morning",
                        "It means goodbye"
                    ],
                    "correct": 1,
                    "explanation": "È un saluto formale. Di solito si ripete la stessa frase."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai salutare le persone nuove con educazione. Prossimo passo: parlare del tuo lavoro.",
                    "xp": 15
                }
            ]
        },
        "talking-about-your-job": {
            "title": "Parlare del tuo lavoro",
            "topic": "Saluti e presentazioni",
            "duration": "7 min",
            "xp": 20,
            "slides": [
                {
                    "type": "intro",
                    "title": "Che lavoro fai?",
                    "body": "Dopo nomi e provenienza, il lavoro è uno degli argomenti più comuni. In inglese \"What do you do?\" significa \"Che lavoro fai?\".",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "What do you do?",
                    "translation": "Chiedere il lavoro di qualcuno",
                    "phonetic": "/wʌt duː juː duː/",
                    "note": "Suona strano, ma significa \"Qual è il tuo lavoro?\". La risposta è un mestiere, non un'attività.",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a teacher.",
                    "translation": "Dire il tuo lavoro",
                    "phonetic": "/aɪm ə ˈtiːtʃər/",
                    "note": "L'inglese richiede \"a\" o \"an\" prima del mestiere: \"I'm a teacher\", mai \"I'm teacher\".",
                    "emoji": "👩‍🏫"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm an engineer.",
                    "translation": "Usare \"an\" davanti a un suono vocalico",
                    "phonetic": "/aɪm ən ˌɛndʒɪˈnɪr/",
                    "note": "Si usa \"an\" davanti a un suono vocalico: an engineer, an artist, an accountant.",
                    "emoji": "🛠️"
                },
                {
                    "type": "vocabulary",
                    "word": "I work in an office.",
                    "translation": "Descrivere dove lavori",
                    "phonetic": "/aɪ wɜːrk ɪn ən ˈɒfɪs/",
                    "note": "Utile se non vuoi dire il tuo lavoro esatto. Schema: \"I work in\" + luogo.",
                    "emoji": "🏢"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm a student.",
                    "translation": "Dire che studi",
                    "phonetic": "/aɪm ə ˈstuːdənt/",
                    "note": "Puoi aggiungere dove: \"I'm a student at the University of Verona.\"",
                    "emoji": "🎓"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm between jobs.",
                    "translation": "Un modo educato per dire che sei disoccupato",
                    "phonetic": "/aɪm bɪˈtwiːn dʒɒbz/",
                    "note": "Suona più morbido e positivo di \"I'm unemployed\".",
                    "emoji": "🔍"
                },
                {
                    "type": "question",
                    "question": "Cosa significa \"What do you do?\" in una conversazione?",
                    "options": [
                        "What are you doing right now?",
                        "What is your job?",
                        "Where do you live?",
                        "How are you?"
                    ],
                    "correct": 1,
                    "explanation": "Chiede qual è il tuo lavoro."
                },
                {
                    "type": "question",
                    "question": "Quale frase è corretta?",
                    "options": [
                        "I'm teacher.",
                        "I'm a teacher.",
                        "I'm the teacher job.",
                        "I teacher."
                    ],
                    "correct": 1,
                    "explanation": "L'inglese richiede \"a\" prima del mestiere."
                },
                {
                    "type": "question",
                    "question": "Quale è corretta?",
                    "options": [
                        "I'm a engineer.",
                        "I'm an engineer.",
                        "I'm engineer.",
                        "I'm the engineer job."
                    ],
                    "correct": 1,
                    "explanation": "\"Engineer\" inizia con un suono vocalico, quindi si usa \"an\"."
                },
                {
                    "type": "question",
                    "question": "Quale frase è un modo educato per dire che al momento non hai un lavoro?",
                    "options": [
                        "I'm between jobs.",
                        "I'm a student.",
                        "I work in an office.",
                        "I'm an engineer."
                    ],
                    "correct": 0,
                    "explanation": "\"Between jobs\" è un modo gentile per dire disoccupato."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai parlare del tuo lavoro e chiedere quello degli altri. Prossimo passo: chiedere l'età.",
                    "xp": 20
                }
            ]
        },
        "asking-someones-age": {
            "title": "Chiedere l'età",
            "topic": "Saluti e presentazioni",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Parlare dell'età",
                    "body": "L'età funziona in modo diverso rispetto all'italiano. In inglese non \"hai\" anni, \"sei\" anni.",
                    "emoji": "🎂"
                },
                {
                    "type": "vocabulary",
                    "word": "How old are you?",
                    "translation": "Chiedere l'età di qualcuno",
                    "phonetic": "/haʊ oʊld ɑːr juː/",
                    "note": "Va bene tra amici o nei moduli. Con adulti che non conosci bene può sembrare indiscreta.",
                    "emoji": "🎂"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm thirty.",
                    "translation": "Dire la tua età",
                    "phonetic": "/aɪm ˈθɜːrti/",
                    "note": "Usa \"I am\" + numero. Puoi aggiungere \"years old\": \"I'm thirty years old.\"",
                    "emoji": "3️⃣"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm twenty-five years old.",
                    "translation": "Un modo completo di dire la tua età",
                    "phonetic": "/aɪm ˈtwɛnti faɪv jɪrz oʊld/",
                    "note": "Non dire mai \"I have twenty-five years\": è un errore comune per chi parla italiano.",
                    "emoji": "🔢"
                },
                {
                    "type": "vocabulary",
                    "word": "How old is she?",
                    "translation": "Chiedere l'età di una terza persona",
                    "phonetic": "/haʊ oʊld ɪz ʃiː/",
                    "note": "Cambia \"are\" in \"is\" per he, she e it.",
                    "emoji": "👩"
                },
                {
                    "type": "vocabulary",
                    "word": "She's in her twenties.",
                    "translation": "Un'età approssimativa",
                    "phonetic": "/ʃiːz ɪn hɜːr ˈtwɛntiz/",
                    "note": "Un modo educato di indicare l'età senza dare un numero preciso.",
                    "emoji": "🧑‍🤝‍🧑"
                },
                {
                    "type": "question",
                    "question": "Quale frase è corretta?",
                    "options": [
                        "I have thirty years.",
                        "I'm thirty years old.",
                        "I am thirty years.",
                        "I have thirty old."
                    ],
                    "correct": 1,
                    "explanation": "In inglese si usa \"be\" per l'età: \"I'm thirty years old.\""
                },
                {
                    "type": "question",
                    "question": "Come si chiede l'età di un uomo?",
                    "options": [
                        "How old are he?",
                        "How old is he?",
                        "How many years has he?",
                        "What age he is?"
                    ],
                    "correct": 1,
                    "explanation": "Con \"he\" si usa \"is\": \"How old is he?\""
                },
                {
                    "type": "question",
                    "question": "Cosa significa \"in her twenties\"?",
                    "options": [
                        "Aged 20 to 29",
                        "Aged exactly 20",
                        "Aged 12",
                        "Aged over 50"
                    ],
                    "correct": 0,
                    "explanation": "\"In her twenties\" significa tra 20 e 29 anni."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai parlare dell'età come in inglese. Prossimo passo: dire per favore e grazie.",
                    "xp": 15
                }
            ]
        },
        "saying-please-and-thank-you": {
            "title": "Dire per favore e grazie",
            "topic": "Saluti e presentazioni",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Essere cortesi in inglese",
                    "body": "Chi parla inglese dice \"please\" e \"thank you\" di continuo, anche nelle piccole situazioni quotidiane. È una parte importante per suonare naturali.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Please",
                    "translation": "Si aggiunge a una richiesta per renderla educata",
                    "phonetic": "/pliːz/",
                    "note": "\"A coffee, please.\" Si può mettere all'inizio o alla fine della richiesta.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Thank you",
                    "translation": "Il modo standard per ringraziare",
                    "phonetic": "/θæŋk juː/",
                    "note": "Sempre appropriato, formale o informale.",
                    "emoji": "😊"
                },
                {
                    "type": "vocabulary",
                    "word": "Thanks a lot",
                    "translation": "Un grazie più caloroso ed entusiasta",
                    "phonetic": "/θæŋks ə lɒt/",
                    "note": "Adatto quando qualcuno ti ha davvero aiutato. \"Thanks so much\" è simile.",
                    "emoji": "🥰"
                },
                {
                    "type": "vocabulary",
                    "word": "You're welcome",
                    "translation": "La risposta standard a thank you",
                    "phonetic": "/jɔːr ˈwɛlkəm/",
                    "note": "Non dire solo \"welcome\". La frase completa è \"You're welcome\".",
                    "emoji": "🤗"
                },
                {
                    "type": "vocabulary",
                    "word": "No problem",
                    "translation": "Una risposta informale a un ringraziamento",
                    "phonetic": "/noʊ ˈprɒbləm/",
                    "note": "Amichevole e rilassato. Anche \"No worries\" funziona, soprattutto nell'inglese britannico e australiano.",
                    "emoji": "👌"
                },
                {
                    "type": "question",
                    "question": "Quale richiesta è la più educata?",
                    "options": [
                        "Water.",
                        "Give me water.",
                        "Water, please.",
                        "I want water."
                    ],
                    "correct": 2,
                    "explanation": "Aggiungere \"please\" rende la richiesta educata."
                },
                {
                    "type": "question",
                    "question": "Qualcuno dice \"Thank you.\" Qual è la risposta corretta?",
                    "options": [
                        "You're welcome.",
                        "Please.",
                        "Hello.",
                        "Goodbye."
                    ],
                    "correct": 0,
                    "explanation": "\"You're welcome\" è la risposta standard."
                },
                {
                    "type": "question",
                    "question": "Quale risposta è la più informale?",
                    "options": [
                        "You're welcome",
                        "It is my pleasure",
                        "No problem",
                        "Thank you very much"
                    ],
                    "correct": 2,
                    "explanation": "\"No problem\" è rilassata e informale."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai essere cortese nell'inglese di tutti i giorni. Prossimo passo: come scusarsi.",
                    "xp": 15
                }
            ]
        },
        "apologizing-and-excusing-yourself": {
            "title": "Scusarsi e chiedere permesso",
            "topic": "Saluti e presentazioni",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Sorry ed excuse me",
                    "body": "L'inglese ha due parole chiave che sembrano simili ma hanno funzioni diverse: \"sorry\" per gli errori e \"excuse me\" per attirare l'attenzione o passare.",
                    "emoji": "🙇"
                },
                {
                    "type": "vocabulary",
                    "word": "Sorry",
                    "translation": "Una scusa rapida dopo un piccolo errore",
                    "phonetic": "/ˈsɒri/",
                    "note": "Si dice quando urti qualcuno o fai un piccolo errore.",
                    "emoji": "😔"
                },
                {
                    "type": "vocabulary",
                    "word": "I'm so sorry",
                    "translation": "Una scusa più forte e sincera",
                    "phonetic": "/aɪm soʊ ˈsɒri/",
                    "note": "Si usa per errori più grandi o per mostrare vero dispiacere.",
                    "emoji": "🙏"
                },
                {
                    "type": "vocabulary",
                    "word": "Excuse me",
                    "translation": "Attirare l'attenzione o passare educatamente",
                    "phonetic": "/ɪkˈskjuːz miː/",
                    "note": "Si usa prima di parlare a uno sconosciuto o quando devi passare.",
                    "emoji": "🙋"
                },
                {
                    "type": "vocabulary",
                    "word": "Pardon?",
                    "translation": "Chiedere a qualcuno di ripetere",
                    "phonetic": "/ˈpɑːrdn/",
                    "note": "Modo educato per dire che non hai sentito. Anche \"Sorry?\" funziona.",
                    "emoji": "👂"
                },
                {
                    "type": "vocabulary",
                    "word": "That's okay",
                    "translation": "Accettare delle scuse",
                    "phonetic": "/ðæts oʊˈkeɪ/",
                    "note": "\"No problem\" e \"Don't worry about it\" sono buone alternative.",
                    "emoji": "😌"
                },
                {
                    "type": "question",
                    "question": "Pesti il piede a qualcuno per sbaglio. Cosa dici?",
                    "options": [
                        "Excuse me",
                        "Sorry",
                        "Pardon the foot",
                        "Please"
                    ],
                    "correct": 1,
                    "explanation": "\"Sorry\" è la scusa rapida dopo un piccolo incidente."
                },
                {
                    "type": "question",
                    "question": "Vuoi chiedere indicazioni a uno sconosciuto. Come inizi?",
                    "options": [
                        "Sorry you",
                        "Excuse me",
                        "I'm so sorry",
                        "Goodbye"
                    ],
                    "correct": 1,
                    "explanation": "\"Excuse me\" attira l'attenzione in modo educato."
                },
                {
                    "type": "question",
                    "question": "Non hai sentito cosa ha detto qualcuno. Cosa puoi dire?",
                    "options": [
                        "Pardon?",
                        "Thank you.",
                        "See you later.",
                        "Nice to meet you."
                    ],
                    "correct": 0,
                    "explanation": "\"Pardon?\" chiede alla persona di ripetere."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai scusarti e attirare l'attenzione con educazione. Prossimo passo: presentare un'altra persona.",
                    "xp": 15
                }
            ]
        },
        "introducing-someone-else": {
            "title": "Presentare un'altra persona",
            "topic": "Saluti e presentazioni",
            "duration": "6 min",
            "xp": 15,
            "slides": [
                {
                    "type": "intro",
                    "title": "Fare le presentazioni",
                    "body": "Presentare due persone è un'abilità sociale utile. In inglese puoi farlo in modo semplice e cordiale.",
                    "emoji": "👥"
                },
                {
                    "type": "vocabulary",
                    "word": "This is my friend, Sara.",
                    "translation": "Il modo più semplice di presentare qualcuno",
                    "phonetic": "/ðɪs ɪz maɪ frɛnd ˈsɑːrə/",
                    "note": "Schema: \"This is\" + relazione + nome.",
                    "emoji": "👭"
                },
                {
                    "type": "vocabulary",
                    "word": "This is my colleague.",
                    "translation": "Presentare una persona con cui lavori",
                    "phonetic": "/ðɪs ɪz maɪ ˈkɒliːɡ/",
                    "note": "\"Colleague\" è la parola neutra per un collega.",
                    "emoji": "💼"
                },
                {
                    "type": "vocabulary",
                    "word": "Let me introduce you to Tom.",
                    "translation": "Una presentazione più educata e formale",
                    "phonetic": "/lɛt miː ˌɪntrəˈdjuːs juː tuː tɒm/",
                    "note": "Adatta a situazioni professionali.",
                    "emoji": "🎩"
                },
                {
                    "type": "vocabulary",
                    "word": "Have you met Anna?",
                    "translation": "Controllare se due persone si conoscono già",
                    "phonetic": "/hæv juː mɛt ˈænə/",
                    "note": "Un modo naturale per iniziare una presentazione a una festa.",
                    "emoji": "❓"
                },
                {
                    "type": "vocabulary",
                    "word": "I'd like you to meet my sister.",
                    "translation": "Un modo caloroso e leggermente formale di presentare qualcuno",
                    "phonetic": "/aɪd laɪk juː tuː miːt maɪ ˈsɪstər/",
                    "note": "Mostra che la presentazione è importante per te.",
                    "emoji": "👩‍👧"
                },
                {
                    "type": "question",
                    "question": "Quale frase presenta un amico nel modo più semplice?",
                    "options": [
                        "This is my friend, Sara.",
                        "Sara my friend is.",
                        "I friend Sara.",
                        "Meet Sara friend."
                    ],
                    "correct": 0,
                    "explanation": "\"This is\" + relazione + nome è lo schema più semplice."
                },
                {
                    "type": "question",
                    "question": "Quale domanda controlla se due persone si conoscono già?",
                    "options": [
                        "Have you met Anna?",
                        "Where is Anna?",
                        "Who are you?",
                        "How old is Anna?"
                    ],
                    "correct": 0,
                    "explanation": "\"Have you met...?\" è il modo naturale di controllare."
                },
                {
                    "type": "question",
                    "question": "Quale frase è la più formale?",
                    "options": [
                        "This is Tom.",
                        "Meet Tom.",
                        "Let me introduce you to Tom.",
                        "Tom, this is Ben."
                    ],
                    "correct": 2,
                    "explanation": "\"Let me introduce you to...\" è l'opzione più educata e formale."
                },
                {
                    "type": "complete",
                    "title": "Lezione completata!",
                    "body": "Ora sai presentare le persone in inglese. Hai completato l'argomento Saluti e presentazioni!",
                    "xp": 15
                }
            ]
        }
    }
};